import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import NewRequisitionSelector from "@/pages/NewRequisitionSelector";
import NewMaterial from "@/pages/NewMaterial";
import NewService from "@/pages/NewService";
import NewEvent from "@/pages/NewEvent";
import NewTransportation from "@/pages/NewTransportation";
import NewFnB from "@/pages/NewFnB";
import NewTravel from "@/pages/NewTravel";
import NewHotel from "@/pages/NewHotel";
import NewGuest from "@/pages/NewGuest";
import NewAdvanceRequisition from "@/pages/NewAdvanceRequisition";
import NewPocketMoney from "@/pages/NewPocketMoney";
import NewAdvanceSettlement from "@/pages/NewAdvanceSettlement";
import NewReimbursement from "@/pages/NewReimbursement";
import NewMaini from "@/pages/NewMaini";
import RequisitionDetail from "@/pages/RequisitionDetail";
import AdminUsers from "@/pages/AdminUsers";
import DevEmailLog from "@/pages/DevEmailLog";
import WorkflowRepairPage from "@/pages/WorkflowRepairPage";
import NotificationsPage from "@/pages/NotificationsPage";
import HodDashboard from "@/pages/HodDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import ProcessOwnerDashboard from "@/pages/ProcessOwnerDashboard";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import TransportTrackerPage from "@/pages/TransportTrackerPage";
import GuestTrackerPage from "@/pages/GuestTrackerPage";
import FnBTrackerPage from "@/pages/FnBTrackerPage";
import DashboardPrintView from "@/pages/DashboardPrintView";
import NewPrintJob from "@/pages/NewPrintJob";
import PrintJobTrackerPage from "@/pages/PrintJobTrackerPage";

/**
 * AuthCallback — processes the Emergent OAuth session_id fragment.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function AuthCallback() {
  const { exchangeSession } = useAuth();
  const location = useLocation();
  useEffect(() => {
    const hash = window.location.hash || "";
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sid) {
      window.location.replace("/login");
      return;
    }
    (async () => {
      try {
        await exchangeSession(sid);
        window.history.replaceState(null, "", window.location.pathname);
        window.location.replace("/dashboard");
      } catch (e) {
        // Phase 11 — distinguish domain-gate 403 from other errors for a
        // cleaner toast and no half-authenticated state.
        const status = e?.response?.status;
        const detail = (e?.response?.data?.detail || "").toString().toLowerCase();
        const isDomainGate = status === 403 && detail.includes("mitgurukul");
        const err = isDomainGate ? "domain" : "session";
        window.location.replace(`/login?err=${err}`);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  return (
    <div className="min-h-screen bg-paper flex items-center justify-center" data-testid="auth-callback-loading">
      <div className="text-[#7a1f2b] font-serif text-lg">Signing you in…</div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/requisitions/new" element={<ProtectedRoute><NewRequisitionSelector /></ProtectedRoute>} />
        <Route path="/requisitions/new/material" element={<ProtectedRoute><NewMaterial /></ProtectedRoute>} />
        <Route path="/requisitions/new/service" element={<ProtectedRoute><NewService /></ProtectedRoute>} />
        <Route path="/requisitions/new/event" element={<ProtectedRoute><NewEvent /></ProtectedRoute>} />
        <Route path="/requisitions/new/transportation" element={<ProtectedRoute><NewTransportation /></ProtectedRoute>} />
        <Route path="/requisitions/new/fnb" element={<ProtectedRoute><NewFnB /></ProtectedRoute>} />
        <Route path="/requisitions/new/travel" element={<ProtectedRoute><NewTravel /></ProtectedRoute>} />
        <Route path="/requisitions/new/hotel" element={<ProtectedRoute><NewHotel /></ProtectedRoute>} />
        <Route path="/requisitions/new/guest" element={<ProtectedRoute><NewGuest /></ProtectedRoute>} />
        <Route path="/requisitions/new/advance_requisition" element={<ProtectedRoute><NewAdvanceRequisition /></ProtectedRoute>} />
        <Route path="/requisitions/new/pocket_money" element={<ProtectedRoute><NewPocketMoney /></ProtectedRoute>} />
        <Route path="/requisitions/new/advance_settlement" element={<ProtectedRoute><NewAdvanceSettlement /></ProtectedRoute>} />
        <Route path="/requisitions/new/reimbursement" element={<ProtectedRoute><NewReimbursement /></ProtectedRoute>} />
        <Route path="/requisitions/new/maini" element={<ProtectedRoute><NewMaini /></ProtectedRoute>} />
        <Route path="/requisitions/new/print_job" element={<ProtectedRoute><NewPrintJob /></ProtectedRoute>} />
        <Route path="/requisitions/:ref" element={<ProtectedRoute><RequisitionDetail /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

        <Route path="/hod" element={<ProtectedRoute roles={["hod"]}><HodDashboard /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute roles={["admin1","admin1_alt","admin2","super_admin"]}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/process-owner" element={<ProtectedRoute roles={["process_owner"]}><ProcessOwnerDashboard /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute roles={["super_admin","admin1","admin1_alt","admin2"]}><AnalyticsDashboard /></ProtectedRoute>} />
        <Route path="/analytics/transport-tracker" element={<ProtectedRoute><TransportTrackerPage /></ProtectedRoute>} />
        <Route path="/analytics/guest-tracker" element={<ProtectedRoute><GuestTrackerPage /></ProtectedRoute>} />
        <Route path="/analytics/fnb-tracker" element={<ProtectedRoute><FnBTrackerPage /></ProtectedRoute>} />
        <Route path="/analytics/print-job-tracker" element={<ProtectedRoute><PrintJobTrackerPage /></ProtectedRoute>} />
        <Route path="/dashboard/print" element={<ProtectedRoute><DashboardPrintView /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute roles={["super_admin"]}><AdminUsers /></ProtectedRoute>} />
        <Route path="/admin/email-log" element={<ProtectedRoute roles={["super_admin"]}><DevEmailLog /></ProtectedRoute>} />
        <Route path="/admin/workflow-repair" element={<ProtectedRoute roles={["super_admin"]}><WorkflowRepairPage /></ProtectedRoute>} />

        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="bottom-right" richColors closeButton />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
